import React, { useState, useEffect, useRef } from "react";
import StatusTable from "./StatusTable";
import MostActiveSector from "./MostActiveSector";
import SectorTrendsExplorer from "./SectorTrendsExplorer";
import SectorPieChart from "./SectorPieChart";
import "./App.css";
import "./esf-theme.css";
import TrackerSummary from "./TrackerSummary";
import { ChevronLeft, ChevronRight, ChevronDown, Search } from "lucide-react";

function App() {
  const [rows, setRows] = useState([]);
  const [monthLabel, setMonthLabel] = useState("");
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [onlyRecent, setOnlyRecent] = useState(true);
  const [showPie, setShowPie] = useState(false);
  const [allSectors, setAllSectors] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [sortMode, setSortMode] = useState("alpha");

  // Temporary buffers for dropdown selections
  const [tempSelectedSectors, setTempSelectedSectors] = useState([]);
  const [tempSelectedCountries, setTempSelectedCountries] = useState([]);

  // Refs for closing dropdowns on outside click
  const sectorDetailsRef = useRef(null);
  const countryDetailsRef = useRef(null);

  const EARLIEST = "2025-04";

  // Notify parent frame about height changes so the app can be embedded in an iframe
  useEffect(() => {
    // Parent pages allowed to receive height messages. The embedding page's
    // origin is read from the referrer; unknown parents get no message.
    const ALLOWED_ORIGINS = [
      "https://www.economicsecurityforum.org",
      "https://economicsecurityforum.org",
      "https://economic-security-forum.webflow.io",
    ];
    let targetOrigin = ALLOWED_ORIGINS[0];
    try {
      const ref = document.referrer ? new URL(document.referrer).origin : "";
      if (ALLOWED_ORIGINS.includes(ref)) targetOrigin = ref;
    } catch (e) {
      /* keep default */
    }

    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage(
        { type: "IFRAME_HEIGHT", frameHeight: height },
        targetOrigin
      );
    };

    // Send initial height
    sendHeight();

    // Observe DOM mutations that might change height
    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Resend height on window resize
    window.addEventListener("resize", sendHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sendHeight);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = selectedMonth ? `?month=${selectedMonth}` : "";
    fetch(`/api/status${qs}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(json => {
        setRows(json.rows);
        setMonthLabel(json.month);
        const months = json.available_months;
        setAvailableMonths(months);
        const sectors = Array.from(new Set(json.rows.map(r => r.sector)))
          .filter(Boolean)
          .sort();
        const countries = Array.from(new Set(json.rows.map(r => r.country)))
          .filter(Boolean)
          .sort();
        setAllSectors(sectors);
        setAllCountries(countries);
        setSelectedSectors(prev =>
          prev.length ? prev.filter(s => sectors.includes(s)) : sectors
        );
        setSelectedCountries(prev =>
          prev.length ? prev.filter(c => countries.includes(c)) : countries
        );
        if (!selectedMonth && months.length) {
          setSelectedMonth(months.slice(-1)[0]);
        }
        setLoading(false);
        setInitialized(true);
      })
      .catch(err => {
        console.error(err);
        setError(err);
        setLoading(false);
        setInitialized(true);
      });
  }, [selectedMonth]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = e => {
      if (
        sectorDetailsRef.current &&
        sectorDetailsRef.current.open &&
        !sectorDetailsRef.current.contains(e.target)
      ) {
        sectorDetailsRef.current.open = false;
        setSelectedSectors(tempSelectedSectors);
      }
      if (
        countryDetailsRef.current &&
        countryDetailsRef.current.open &&
        !countryDetailsRef.current.contains(e.target)
      ) {
        countryDetailsRef.current.open = false;
        setSelectedCountries(tempSelectedCountries);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [tempSelectedSectors, tempSelectedCountries]);

  // Equalize card heights on initial render
  useEffect(() => {
    const cards = document.querySelectorAll('.cards-wrapper > *');
    if (cards.length < 2) return;
    const maxHeight = Math.max(...Array.from(cards, el => el.offsetHeight));
    cards.forEach(el => {
      el.style.minHeight = `${maxHeight}px`;
    });
  }, [rows]);

  if (!initialized && loading) return <p className="App">Loading…</p>;
  if (error) return <p className="App">Error: {error.message}</p>;
  if (!rows.length) return <p className="App">This tool is under renovation.</p>;

  // Apply filters
  let visible = rows.filter(r =>
    r.company.toLowerCase().includes(searchText.toLowerCase()) ||
    r.ticker.toLowerCase().includes(searchText.toLowerCase())
  );
  if (onlyRecent)
    visible = visible.filter(
      r => (r.monthly_status || "no_data").toLowerCase() !== "no_data"
    );
  visible = visible.filter(r => selectedSectors.includes(r.sector));
  visible = visible.filter(r => selectedCountries.includes(r.country));

  if (sortMode === "alpha") {
    visible = [...visible].sort((a, b) => a.company.localeCompare(b.company));
  } else if (sortMode === "status") {
    const order = { investing: 0, withdrawing: 1, no_data: 2 };
    visible = [...visible].sort((a, b) => {
      const sa = (a.monthly_status || "no_data").toLowerCase();
      const sb = (b.monthly_status || "no_data").toLowerCase();
      if (order[sa] !== order[sb]) return order[sa] - order[sb];
      return a.company.localeCompare(b.company);
    });
  }

  // Navigation
  const idx = availableMonths.indexOf(selectedMonth);
  const earliestIdx = Math.max(0, availableMonths.indexOf(EARLIEST));
  const canNext = idx < availableMonths.length - 1;
  const canPrev = idx > earliestIdx;
  const goNext = () => canNext && setSelectedMonth(availableMonths[idx + 1]);
  const goPrev = () => canPrev && setSelectedMonth(availableMonths[idx - 1]);

  const latestMonth = availableMonths.slice(-1)[0];
  const isLatest = selectedMonth === latestMonth;
  const timeframeLabel = isLatest ? "the last 30 days" : monthLabel;

  const showSector =
    selectedSectors.length > 1 && selectedSectors.length < allSectors.length;
  const showCountry =
    selectedCountries.length > 1 &&
    selectedCountries.length < allCountries.length;

  const sectorSummary =
    selectedSectors.length === allSectors.length
      ? "All sectors"
      : selectedSectors.length === 0
      ? "None selected"
      : `${selectedSectors.length} selected`;
  const countrySummary =
    selectedCountries.length === allCountries.length
      ? "All countries"
      : selectedCountries.length === 0
      ? "None selected"
      : `${selectedCountries.length} selected`;

  const onSectorsToggle = e => {
    if (e.target.open) setTempSelectedSectors(selectedSectors);
    else setSelectedSectors(tempSelectedSectors);
  };
  const onCountriesToggle = e => {
    if (e.target.open) setTempSelectedCountries(selectedCountries);
    else setSelectedCountries(tempSelectedCountries);
  };

  return (
    <div className="App">
      <section className="about-section">
        <h1 className="about-title">About the tracker</h1>
        <p className="about-text">
          We take the April 2025 tariff announcement as the starting date of our monitoring activities. Not all corporate decisions, that the tracker identifies, are directly related to the tariff announcements. But the ambition is clear: The US government wants foreign companies to produce and invest more in the US. Therefore, the tracker indicates if and to what extent{" "}
          <span
            className="stoxx-hover"
            onMouseEnter={() => setShowPie(true)}
            onMouseLeave={() => setShowPie(false)}
          >
            STOXX{"\u00A0"}EUROPE{"\u00A0"}600{" "}
            {showPie && (
              <div className="pie-tooltip">
                <SectorPieChart />
              </div>
            )}
          </span>
          companies align their activities with this goal or reduce their footprint in the United States.
        </p>
      </section>
      <section className="analysis-section">
        <h2 className="analysis-title">Data extracted through LLM analysis</h2>
        <p className="analysis-text">
          CRT results need to be interpreted with caution. Our models sift thousands of sources to surface meaningful signals. But occasionally, these signals are mixed and produce ambiguous results. In addition, it is still too early to tell how corporate announcements regarding US business activities will materialise, also because continued regulatory change seems to be the new normal in the US. Therefore, the CRT results need to be considered as a starting point that illustrates tendencies that need further exploration. 
        </p>
      </section>
      <div className="timeframe-pill">
        <span className="timeframe-text">Select time frame:</span>
        <div className="timeframe-inner">
          <button
            className="month-btn gold-button"
            onClick={goPrev}
            disabled={!canPrev}
            aria-label="Previous month"
            title="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="month-current">{monthLabel}</span>
          <button
            className="month-btn gold-button"
            onClick={goNext}
            disabled={!canNext}
            aria-label="Next month"
            title="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div className="cards-wrapper">
        <MostActiveSector rows={rows} isLatest={isLatest} monthLabel={monthLabel} />
        <SectorTrendsExplorer rows={rows} isLatest={isLatest} monthLabel={monthLabel} />
      </div>
      <div className="table-container">
        <TrackerSummary rows={rows} isLatest={isLatest} monthLabel={monthLabel} />
        <h2 className="table-title">Browse all corporate actions from {timeframeLabel}</h2>
        <div className="controls-wrapper">
          <div className="controls">
          <div className="search-section">
            <label htmlFor="company-search" className="search-label">Search for company or ticker</label>
            <div className="search-wrapper">
              <input
                id="company-search"
                type="text"
                className="search-input"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
              <Search className="search-icon" size={19} />
            </div>
          </div>
          <div className="toggle-section">
            <div className="toggle-wrapper">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={onlyRecent}
                  onChange={e => setOnlyRecent(e.target.checked)}
                />
                <span className="slider" />
              </label>
              <span className="toggle-label">Only show relevant actions</span>
            </div>
          </div>
        </div>
        <div className="filters">
          <div className="filter-group">
            <details
              className="filter-dropdown"
              onToggle={onSectorsToggle}
              ref={sectorDetailsRef}
            >
              <summary>
                {`Sector: ${sectorSummary}`}
                <ChevronDown className="chevron" size={16} />
              </summary>
              <div className="dropdown-menu">
                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={tempSelectedSectors.length === allSectors.length}
                    onChange={e =>
                      setTempSelectedSectors(
                        e.target.checked ? allSectors : []
                      )
                    }
                  />
                  All
                </label>
                {allSectors.map(s => (
                  <label key={s} className="checkbox-option">
                    <input
                      type="checkbox"
                      checked={tempSelectedSectors.includes(s)}
                      onChange={e => {
                        if (e.target.checked)
                          setTempSelectedSectors([
                            ...tempSelectedSectors,
                            s,
                          ]);
                        else
                          setTempSelectedSectors(
                            tempSelectedSectors.filter(x => x !== s)
                          );
                      }}
                    />
                    {s}
                  </label>
                ))}
              </div>
            </details>
          </div>
          <div className="filter-group">
            <details
              className="filter-dropdown"
              onToggle={onCountriesToggle}
              ref={countryDetailsRef}
            >
              <summary>
                {`Country: ${countrySummary}`}
                <ChevronDown className="chevron" size={16} />
              </summary>
              <div className="dropdown-menu">
                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={
                      tempSelectedCountries.length === allCountries.length
                    }
                    onChange={e =>
                      setTempSelectedCountries(
                        e.target.checked ? allCountries : []
                      )
                    }
                  />
                  All
                </label>
                {allCountries.map(c => (
                  <label key={c} className="checkbox-option">
                    <input
                      type="checkbox"
                      checked={tempSelectedCountries.includes(c)}
                      onChange={e => {
                        if (e.target.checked)
                          setTempSelectedCountries([
                            ...tempSelectedCountries,
                            c,
                          ]);
                        else
                          setTempSelectedCountries(
                            tempSelectedCountries.filter(x => x !== c)
                          );
                      }}
                    />
                    {c}
                  </label>
                ))}
              </div>
            </details>
          </div>
          <div className="filter-group sort-group">
            <select
              id="sort-select"
              className="sort-select"
              value={sortMode}
              onChange={e => setSortMode(e.target.value)}
              aria-label="Sort by"
            >
              <option value="alpha">Sort by: Alphabetical</option>
              <option value="status">Sort by: Status</option>
            </select>
          </div>
        </div>
      </div>
      <div className="table-scroll">
        <StatusTable
          rows={visible}
          showSector={showSector}
          showCountry={showCountry}
        />
      </div>
      </div>
    </div>
  );
}

export default App;
